import { useState, useContext, useEffect, useCallback, useRef } from "react";
import classes from "./Management.module.scss";
import garage from "../GarageContent/GarageContent.module.scss";
import { getInspections, updateInspectionRequest, bulkUpdateInspectionStatus } from "../../services";
import AuthContext from "../../store/auth-context";
import Upload from "../Layout/Upload";
import AuctionCarCard, { plain, carTitle } from "../GarageContent/AuctionCarCard";
import MediaGallery from "../GarageContent/MediaGallery";
import { formatDay } from "../../services/dealerGarage";

// Status chip colours, from the garage's inspection cards.
const STATUS_TONE = {
  PENDING: garage.toneWarning,
  PROCESSING: garage.toneInfo,
  COMPLETED: garage.toneSuccess,
  CANCELLED: garage.toneDanger,
};

// The car page opens in a new tab so the list, its filter and the ticks stay put.
const NEW_TAB = { target: "_blank", rel: "noopener noreferrer" };

// Requests made longer ago than this move to the Archived tab, whatever their
// status. Only the page splits them; nothing changes in the database.
const ARCHIVE_AFTER_DAYS = 7;
const TABS = [
  { id: "active", label: "Active" },
  { id: "archived", label: "Archived", title: `Requested more than ${ARCHIVE_AFTER_DAYS} days ago` },
];

const AUTOFUTURE_URL = "https://af-app.jthing.com";
const yen = (n) => `¥${Number(n).toLocaleString("en-US")}`;

// The inspection's AutoFuture request (autofutureWorker.js) in a few words.
// Inspections that never went to AutoFuture have no block and show nothing.
const autofutureStatus = (af) => {
  if (!af?.state) return null;
  switch (af.state) {
    case "QUEUED":
      return { text: af.note ? `waiting: ${af.note}` : "queued" };
    case "SUBMITTED":
      return { text: af.linked ? "linked (filed by hand)" : `filed${af.price ? ` ${yen(af.price)}` : ""}`, request: true, note: af.note };
    case "IMPORTED":
      return { text: `imported${af.importedAt ? ` ${formatDay(af.importedAt)}` : ""}`, request: true, tone: classes.autofutureDone };
    case "FAILED":
      return { text: `failed: ${af.error || "no reason given"}`, note: "Set Processing again to retry", tone: classes.autofutureFailed };
    default:
      return { text: af.state.toLowerCase() };
  }
};

// Setting Processing queues the request again unless it is already filed
// (cars.controller's queueAutofutureRequests); the card follows suit.
const queueAutofuture = (inspection) => {
  const { state, error, note, ...rest } = inspection.autofuture || {};
  if (state === "SUBMITTED" || state === "IMPORTED") return inspection;
  return { ...inspection, autofuture: { ...rest, state: "QUEUED", queuedAt: new Date().toISOString() } };
};

const InspectionManagement = () => {
  const [inspections, setInspections] = useState([]);
  const [activeFilter, setActiveFilter] = useState(["PENDING", "PROCESSING"]);
  const [activeTab, setActiveTab] = useState("active");
  const authCtx = useContext(AuthContext);
  const [loading, setLoading] = useState(false);

  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [selectedInspection, setSelectedInspection] = useState(null);
  const uploadRef = useRef(null);

  // The uploader sits under the whole list; bring it to the card's Upload click.
  useEffect(() => {
    if (isUploadModalOpen) uploadRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [isUploadModalOpen, selectedInspection]);

  // Cards whose uploaded photos and videos are shown. Collapsed by default so a
  // full list does not load every inspection's media at once.
  const [mediaOpenIds, setMediaOpenIds] = useState([]);

  const toggleMedia = (id) => {
    setMediaOpenIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  // Inspection notes being written, by inspection id: a card is editing while its
  // id is here, so several can be open without losing what was typed.
  const [notesDrafts, setNotesDrafts] = useState({});
  const [notesSavingId, setNotesSavingId] = useState(null);
  const [notesErrors, setNotesErrors] = useState({});

  const without = (map, id) => {
    const { [id]: _, ...rest } = map;
    return rest;
  };

  const editNotes = (inspection) => {
    setNotesDrafts((prev) => ({ ...prev, [inspection._id]: inspection.notes || "" }));
  };

  const closeNotes = (id) => {
    setNotesDrafts((prev) => without(prev, id));
    setNotesErrors((prev) => without(prev, id));
  };

  // Saving notes sends the customer nothing; they see them in My Garage once the
  // inspection is Completed.
  const saveNotes = (inspection) => {
    const id = inspection._id;
    const notes = (notesDrafts[id] || "").trim();
    setNotesSavingId(id);
    setNotesErrors((prev) => without(prev, id));
    updateInspectionRequest({ carId: inspection.carId, userId: inspection.userId, notes })
      .then((res) => {
        if (res?.message !== "Inspection updated successfully") throw new Error(res?.message || "Not saved");
        setInspections((prev) => prev.map((i) => (i._id === id ? { ...i, notes: res.data?.notes ?? notes } : i)));
        closeNotes(id);
      })
      .catch((err) => {
        setNotesErrors((prev) => ({ ...prev, [id]: `Not saved: ${err?.response?.data?.message || err?.message || "request failed"}` }));
      })
      .finally(() => setNotesSavingId(null));
  };

  // Rows ticked for a bulk status change, and what the last one did.
  const [checkedIds, setCheckedIds] = useState([]);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkResult, setBulkResult] = useState("");

  const changeStatus = (id, carId, userId, status) => {
    setInspections(
      inspections.map((inspection) => {
        if (inspection._id !== id) return inspection;
        const updated = { ...inspection, status: status };
        return status === "PROCESSING" ? queueAutofuture(updated) : updated;
      })
    );
    updateInspectionRequest({ carId, userId, status }).then((res) => { 
      if (res?.message === "Inspection updated successfully") {
        console.log("Inspection Updated");
      } else {
        console.log("Inspection not Updated");
      }
    });
  };

  const fetchInspections = useCallback(() => {
    setLoading(true);
    getInspections().then((res) => {
      setLoading(false);
      console.log("Inspections: ", res);
      if (res?.message === "All inspections retrieved successfully") {
        if (res?.data.length > 0) {
          setInspections(res?.data);
        } else {
          setInspections([]);
        }
      } else {
        setInspections([]);
      }
    });
  }, []);

  useEffect(() => {
    if (authCtx.isLoggedIn) {
      fetchInspections();
    }
  }, [fetchInspections, authCtx.isLoggedIn]);

  // After an upload: reload the list in place and open that card's media so the
  // new files show. A failed reload keeps the list that is on screen.
  const showUploadedMedia = (id) => {
    getInspections().then((res) => {
      if (res?.message !== "All inspections retrieved successfully" || !Array.isArray(res.data)) return;
      setInspections(res.data);
      setMediaOpenIds((prev) => (prev.includes(id) ? prev : [...prev, id]));
    });
  };
  
  const handleFilterClick = (status) => {
    setActiveFilter(prevFilters => {
      if (prevFilters.includes(status)) {
        return prevFilters.filter(f => f !== status);
      } else {
        return [...prevFilters, status];
      }
    });
  };

  const statusInspections = inspections.filter((inspection) =>
    activeFilter.length === 0 ? true : activeFilter.includes(inspection.status)
  );

  // A request with no date can't be aged, so it stays Active.
  const archiveBefore = Date.now() - ARCHIVE_AFTER_DAYS * 24 * 60 * 60 * 1000;
  const tabOf = (inspection) =>
    inspection.createdAt && new Date(inspection.createdAt).getTime() < archiveBefore ? "archived" : "active";
  const tabCounts = { active: 0, archived: 0 };
  statusInspections.forEach((inspection) => { tabCounts[tabOf(inspection)] += 1; });
  const filteredInspections = statusInspections.filter((inspection) => tabOf(inspection) === activeTab);

  // Ticks don't follow you to the other tab.
  const selectTab = (tab) => {
    setActiveTab(tab);
    setCheckedIds([]);
  };

  // Only ticked rows the current filter shows are acted on, so a row hidden by the
  // filter can't change status without anyone seeing it.
  const checkedInspections = filteredInspections.filter((inspection) => checkedIds.includes(inspection._id));
  const checkedCustomerCount = new Set(checkedInspections.map((inspection) => inspection.userId)).size;
  const allVisibleChecked = filteredInspections.length > 0 && checkedInspections.length === filteredInspections.length;

  const toggleChecked = (id) => {
    setCheckedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleAllVisible = () => {
    setCheckedIds(allVisibleChecked ? [] : filteredInspections.map((inspection) => inspection._id));
  };

  const plural = (n, word) => `${n} ${word}${n === 1 ? "" : "s"}`;

  // Sends one email per customer for every ticked car, instead of one per car.
  const changeStatusBulk = (status) => {
    const ids = checkedInspections.map((inspection) => inspection._id);
    if (!ids.length) return;
    setBulkSaving(true);
    setBulkResult("");
    bulkUpdateInspectionStatus({ inspectionIds: ids, status })
      .then((res) => {
        if (res?.message !== "Inspections updated successfully") {
          setBulkResult(`Not updated: ${res?.message || "unknown error"}`);
          return;
        }
        const { updatedIds = [], unchanged = 0, customers = 0, customersEmailed = 0 } = res.data || {};
        setInspections((prev) =>
          prev.map((inspection) => {
            const updated = updatedIds.includes(inspection._id) ? { ...inspection, status } : inspection;
            // Every ticked one is queued, the ones already Processing too.
            return status === "PROCESSING" && ids.includes(inspection._id) ? queueAutofuture(updated) : updated;
          })
        );
        setCheckedIds([]);
        const parts = [`Set ${plural(updatedIds.length, "inspection")} to ${status}`];
        if (customers) parts.push(`emailed ${customersEmailed} of ${plural(customers, "customer")}`);
        if (unchanged) parts.push(`${unchanged} already ${status}, no email`);
        setBulkResult(parts.join(" · "));
      })
      .catch((err) => setBulkResult(`Not updated: ${err?.message || "request failed"}`))
      .finally(() => setBulkSaving(false));
  };

  const renderCard = (inspection) => {
    const checked = checkedIds.includes(inspection._id);
    const lot = plain(inspection.carData?.bidDetail?.bidNo).replace(/^No\.?\s*/i, "");
    const email = inspection.userData?.email || "N/A";
    const af = inspection.autofuture;
    const afStatus = autofutureStatus(af);
    const images = inspection.images || [];
    const videos = inspection.videos || [];
    const mediaOpen = mediaOpenIds.includes(inspection._id);
    const notesDraft = notesDrafts[inspection._id];
    const notesSaving = notesSavingId === inspection._id;
    return (
      <AuctionCarCard
        key={inspection._id || inspection.id}
        car={inspection.carData}
        linkProps={NEW_TAB}
        className={checked ? classes.inspectionCardChecked : undefined}
        overlay={
          <label className={classes.cardSelect}>
            <input
              type="checkbox"
              aria-label={`Select inspection for lot ${lot || inspection._id}`}
              checked={checked}
              onChange={() => toggleChecked(inspection._id)}
            />
            Select
          </label>
        }
      >
        <div className={classes.requestPanel}>
          <div className={classes.requestHead}>
            <span className={classes.requestEmail} title={email}>{email}</span>
            <span className={`${garage.status} ${STATUS_TONE[inspection.status] || garage.toneMuted}`}>
              {inspection.status}
            </span>
          </div>
          {inspection.createdAt && (
            <span className={classes.requestDate}>Requested {formatDay(inspection.createdAt)}</span>
          )}
          {afStatus && (
            <p
              className={`${classes.requestAutofuture} ${afStatus.tone || ""}`}
              title={[af.auction, af.auctionDate, af.lotNo && `lot ${af.lotNo}`, af.checkedAt && `checked ${formatDay(af.checkedAt)}`].filter(Boolean).join(" · ") || undefined}
            >
              <b>AutoFuture:</b> {afStatus.text}
              {afStatus.request && af.requestId && (
                <>
                  {" · "}
                  <a href={`${AUTOFUTURE_URL}/?edit&id=${encodeURIComponent(af.requestId)}`} {...NEW_TAB}>
                    request {af.requestId}
                  </a>
                </>
              )}
              {afStatus.note && <span className={classes.autofutureNote}> · {afStatus.note}</span>}
            </p>
          )}
          {/* What was uploaded here or imported from AutoFuture, whatever the status;
              the customer only sees it once the inspection is Completed. */}
          {images.length > 0 || videos.length > 0 ? (
            <div className={classes.requestMedia}>
              <button
                type="button"
                className={classes.requestMediaToggle}
                aria-expanded={mediaOpen}
                onClick={() => toggleMedia(inspection._id)}
              >
                <i className="fas fa-images" aria-hidden="true"></i>
                <span className={classes.requestMediaCounts}>
                  {[images.length && plural(images.length, "photo"), videos.length && plural(videos.length, "video")].filter(Boolean).join(" · ")}
                </span>
                <span className={classes.requestMediaAction}>
                  {mediaOpen ? "Hide" : "View"}
                  <i className={`fas fa-chevron-${mediaOpen ? "up" : "down"}`} aria-hidden="true"></i>
                </span>
              </button>
              {mediaOpen && (
                <MediaGallery images={images} videos={videos} title={carTitle(inspection.carData)} showCounts={false} />
              )}
            </div>
          ) : (
            <span className={classes.requestMediaNone}>No photos or videos uploaded yet</span>
          )}
          {notesDraft !== undefined ? (
            <div className={classes.requestNotes}>
              <label htmlFor={`notes-${inspection._id}`} className={classes.requestNotesLabel}>Inspection notes</label>
              <textarea
                id={`notes-${inspection._id}`}
                className={classes.requestNotesInput}
                rows={7}
                value={notesDraft}
                placeholder="What the inspection found, written for the customer"
                disabled={notesSaving}
                autoFocus
                onChange={(e) => setNotesDrafts((prev) => ({ ...prev, [inspection._id]: e.target.value }))}
              />
              <span className={classes.requestNotesHint}>The customer sees these in My Garage once the inspection is Completed.</span>
              {notesErrors[inspection._id] && (
                <span className={classes.requestNotesError} role="alert">{notesErrors[inspection._id]}</span>
              )}
              <div className={classes.requestNotesButtons}>
                <button type="button" className={classes.formButtonSecondary} disabled={notesSaving} onClick={() => closeNotes(inspection._id)}>
                  Cancel
                </button>
                <button type="button" className={classes.formButtonPrimary} disabled={notesSaving} onClick={() => saveNotes(inspection)}>
                  {notesSaving ? "Saving…" : "Save notes"}
                </button>
              </div>
            </div>
          ) : inspection.notes ? (
            <div className={classes.requestNotes}>
              <div className={classes.requestNotesHead}>
                <span className={classes.requestNotesLabel}>Inspection notes</span>
                <button type="button" className={classes.requestNotesEdit} onClick={() => editNotes(inspection)}>
                  <i className="fas fa-pen" aria-hidden="true"></i> Edit
                </button>
              </div>
              <p className={classes.requestNotesText}>{inspection.notes}</p>
            </div>
          ) : (
            <button type="button" className={classes.requestNotesAdd} onClick={() => editNotes(inspection)}>
              <i className="fas fa-plus" aria-hidden="true"></i> Add inspection notes
            </button>
          )}
          <div className={`${classes.requestActions} ${inspection.images?.length > 0 ? classes.imagesExist : ""}`}>
            <button
              className={classes.tableButton}
              onClick={() => changeStatus(inspection._id, inspection.carId, inspection.userId, "PROCESSING")}
            >
              Set Processing
            </button>
            <button
              className={`${classes.tableButton} ${classes.tableSuccessButton}`}
              onClick={() => changeStatus(inspection._id, inspection.carId, inspection.userId, "COMPLETED")}
            >
              Set Completed
            </button>
            <button
              className={`${classes.tableButton} ${classes.tableDangerButton}`}
              onClick={() => changeStatus(inspection._id, inspection.carId, inspection.userId, "CANCELLED")}
            >
              Set Cancelled
            </button>
            <button
              className={classes.fileUploadLabel}
              onClick={() => {
                setSelectedInspection(inspection);
                setIsUploadModalOpen(true);
              }}
            >
              <i className="fas fa-upload"></i> Upload Images
            </button>
          </div>
        </div>
      </AuctionCarCard>
    );
  };

  return (
    <div className="container">
      <h1 className={classes.h1}>Inspection Management</h1>
      <div className="container mx-auto">
        <div className={garage.tabs} role="tablist">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              title={tab.title}
              className={`${garage.tab} ${activeTab === tab.id ? garage.tabActive : ""}`}
              onClick={() => selectTab(tab.id)}
            >
              {tab.label}
              {!loading && <span className={garage.count}>{tabCounts[tab.id]}</span>}
            </button>
          ))}
        </div>
        <div className={classes.filterButtons}>
          <span><b>Filter by status: </b></span>
          {["PENDING", "PROCESSING", "COMPLETED", "CANCELLED"].map((status) => (
            <button
              key={status}
              style={{
                backgroundColor: activeFilter.includes(status) ? "#7962e8" : "#f8f9fa",
                color: activeFilter.includes(status) ? "white" : "#000",
                fontFamily: "Poppins, sans-serif",
                fontSize: "12px",
                padding: "8px 15px",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                marginLeft: "15px",
                transition: "background-color 0.3s ease, color 0.3s ease",
              }}
              onClick={() => handleFilterClick(status)}
            >
              {status}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="text-center py-4">Loading inspections...</div>
        ) : filteredInspections.length === 0 ? (
          <p className={classes.inspectionNone}>
            {activeTab === "archived"
              ? `No inspections requested more than ${ARCHIVE_AFTER_DAYS} days ago match the filter.`
              : "No inspections match the filter."}
          </p>
        ) : (
          <>
            <label className={classes.selectAll}>
              <input
                type="checkbox"
                checked={allVisibleChecked}
                onChange={toggleAllVisible}
              />
              Select all {plural(filteredInspections.length, "inspection")} shown
            </label>
            <div className={classes.inspectionGrid}>
              {filteredInspections.map(renderCard)}
            </div>
          </>
        )}
        {(checkedInspections.length > 0 || bulkResult) && (
          <div className={classes.bulkBar} role="region" aria-label="Bulk status update">
            {checkedInspections.length > 0 ? (
              <>
                <span className={classes.bulkSummary}>
                  {plural(checkedInspections.length, "inspection")} selected · {plural(checkedCustomerCount, "customer")} · one email each
                </span>
                <div className={classes.formButtons}>
                  <button className={classes.tableButton} disabled={bulkSaving} onClick={() => changeStatusBulk("PROCESSING")}>
                    Set Processing
                  </button>
                  <button className={`${classes.tableButton} ${classes.tableSuccessButton}`} disabled={bulkSaving} onClick={() => changeStatusBulk("COMPLETED")}>
                    Set Completed
                  </button>
                  <button className={`${classes.tableButton} ${classes.tableDangerButton}`} disabled={bulkSaving} onClick={() => changeStatusBulk("CANCELLED")}>
                    Set Cancelled
                  </button>
                  <button className={classes.formButtonSecondary} disabled={bulkSaving} onClick={() => setCheckedIds([])}>
                    Clear
                  </button>
                </div>
              </>
            ) : (
              <>
                <span className={classes.bulkSummary}>{bulkResult}</span>
                <button className={classes.formButtonSecondary} onClick={() => setBulkResult("")}>
                  Dismiss
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {/* Unmounting on close is what lets the uploader be opened again for the SAME
          inspection: Upload owns one Uppy instance per mount, so a fresh mount is a
          fresh picker. */}
      {isUploadModalOpen && (
        <div ref={uploadRef} className={classes.uploadPanel}>
          <Upload
            carId={selectedInspection.carId}
            inspectionId={selectedInspection._id}
            onClose={() => setIsUploadModalOpen(false)}
            onUploaded={() => showUploadedMedia(selectedInspection._id)}
          />
        </div>
      )}
    </div>
  );
};

export default InspectionManagement;
